import React, { useState, useEffect } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/firebase-manager';
import {
  FaCheckCircle,
  FaSpinner,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaBriefcase,
  FaFileAlt,
  FaLink,
  FaLinkedin,
  FaCommentDots,
  FaBullhorn,
  FaPaperPlane,
  FaCloudUploadAlt,
} from 'react-icons/fa';

const POSITION_OPTIONS = [
  'Marketing Manager',
  'Content Writer',
  'Social Media Manager',
  'Brand Manager',
  'Growth Marketing',
  'Other',
];

const HOW_DID_YOU_HEAR_OPTIONS = [
  'LinkedIn',
  'Indeed',
  'Company Website',
  'Referral',
  'Other',
];

type FormVariant = 'careers' | 'collegeBrandAmbassador';

interface MarketingJobFormProps {
  variant?: FormVariant;
  onTrackSubmit?: () => void;
}

const inputBase = 'w-full px-4 py-3 bg-WHITE/[0.06] border border-WHITE/15 rounded-xl text-WHITE placeholder-WHITE/40 focus:outline-none focus:border-blue-400/50 focus:bg-WHITE/[0.08] transition-all duration-200 text-sm';
const selectBase = 'w-full px-4 py-3 bg-WHITE/[0.06] border border-WHITE/15 rounded-xl text-WHITE focus:outline-none focus:border-blue-400/50 focus:bg-WHITE/[0.08] transition-all duration-200 text-sm';

const FieldLabel: React.FC<{ icon: React.ReactNode; text: string; required?: boolean }> = ({ icon, text, required }) => (
  <label className="flex items-center gap-2 text-WHITE/80 text-xs font-semibold mb-1.5 tracking-wide uppercase">
    {icon}
    {text}{required && <span className="text-blue-400">*</span>}
  </label>
);

const MarketingJobForm: React.FC<MarketingJobFormProps> = ({ variant = 'careers', onTrackSubmit }) => {
  const isCollegeAmbassador = variant === 'collegeBrandAmbassador';

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: isCollegeAmbassador ? 'College Brand Ambassador' : '',
    resumeUrl: '',
    portfolioUrl: '',
    linkedInUrl: '',
    coverLetter: '',
    howDidYouHear: '',
  });

  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isCollegeAmbassador) {
      setFormData((prev) => ({ ...prev, position: 'College Brand Ambassador' }));
    }
  }, [isCollegeAmbassador]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type) && !file.name.match(/\.(pdf|doc|docx)$/i)) {
        setError('Please upload a PDF or Word document.');
        return;
      }
      if (file.size > 5000000) {
        setError('File size must be under 5MB.');
        return;
      }
      setResumeFile(file);
      setError('');
    }
  };

  const uploadResume = async (file: File): Promise<string> => {
    const fileName = `job-applications/resumes/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    const storageRef = ref(storage, fileName);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let resumeUrl = '';
      if (!isCollegeAmbassador && resumeFile) {
        resumeUrl = await uploadResume(resumeFile);
      }

      const position = isCollegeAmbassador ? 'College Brand Ambassador' : formData.position;

      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl || apiUrl.trim() === '') {
        throw new Error('Job applications are not available at this time. Please try again later or contact support@collectedaf.com.');
      }

      const response = await fetch(`${apiUrl.replace(/\/$/, '')}/api/v1/web-app/job-applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone || '',
          position,
          resumeUrl: isCollegeAmbassador ? '' : resumeUrl || '',
          portfolioUrl: isCollegeAmbassador ? '' : formData.portfolioUrl || '',
          linkedInUrl: isCollegeAmbassador ? '' : formData.linkedInUrl || '',
          coverLetter: isCollegeAmbassador ? '' : formData.coverLetter.slice(0, 10000) || '',
          howDidYouHear: isCollegeAmbassador ? '' : formData.howDidYouHear || '',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const msg = data?.errors?.[0]?.msg ?? data?.error ?? 'Failed to submit application.';
        throw new Error(msg);
      }

      if (data.success) {
        onTrackSubmit?.();
        setShowThankYou(true);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          position: isCollegeAmbassador ? 'College Brand Ambassador' : '',
          resumeUrl: '',
          portfolioUrl: '',
          linkedInUrl: '',
          coverLetter: '',
          howDidYouHear: '',
        });
        setResumeFile(null);
      } else {
        throw new Error(data?.error ?? 'Failed to submit application.');
      }
    } catch (err) {
      console.error('Job application error:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit your application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-5 max-w-2xl mx-auto">
        {/* Personal Information */}
        <div className="bg-WHITE/[0.04] rounded-2xl p-5 border border-WHITE/10">
          <h3 className="text-WHITE text-sm font-bold tracking-wide mb-4 flex items-center gap-2">
            <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'rgba(59,130,246,0.2)' }}>
              <FaUser size={10} style={{ color: '#60a5fa' }} />
            </div>
            Personal Information
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <FieldLabel icon={<FaUser size={10} style={{ color: '#60a5fa' }} />} text="First Name" required />
              <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required className={inputBase} placeholder="John" />
            </div>
            <div>
              <FieldLabel icon={<FaUser size={10} style={{ color: '#60a5fa' }} />} text="Last Name" required />
              <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required className={inputBase} placeholder="Doe" />
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <div>
              <FieldLabel icon={<FaEnvelope size={10} style={{ color: '#60a5fa' }} />} text="Email" required />
              <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className={inputBase} placeholder="john@example.com" />
            </div>
            <div>
              <FieldLabel icon={<FaPhone size={10} style={{ color: '#60a5fa' }} />} text="Phone" />
              <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className={inputBase} placeholder="+1 (555) 123-4567" />
            </div>
          </div>
        </div>

        {/* Position */}
        <div className="bg-WHITE/[0.04] rounded-2xl p-5 border border-WHITE/10">
          <h3 className="text-WHITE text-sm font-bold tracking-wide mb-4 flex items-center gap-2">
            <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'rgba(16,185,129,0.2)' }}>
              <FaBriefcase size={10} style={{ color: '#34d399' }} />
            </div>
            Position
          </h3>
          {isCollegeAmbassador ? (
            <div>
              <FieldLabel icon={<FaBriefcase size={10} style={{ color: '#34d399' }} />} text="Role" />
              <input type="text" id="position" value="College Brand Ambassador" readOnly disabled className={`${inputBase} opacity-60 cursor-not-allowed`} />
            </div>
          ) : (
            <div>
              <FieldLabel icon={<FaBriefcase size={10} style={{ color: '#34d399' }} />} text="Select Position" required />
              <select id="position" name="position" value={formData.position} onChange={handleChange} required className={selectBase}>
                <option value="" className="bg-BLACK text-WHITE/60">Select position</option>
                {POSITION_OPTIONS.map((opt) => (
                  <option key={opt} value={opt} className="bg-BLACK">{opt}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Documents & Links — careers only */}
        {!isCollegeAmbassador && (
          <div className="bg-WHITE/[0.04] rounded-2xl p-5 border border-WHITE/10">
            <h3 className="text-WHITE text-sm font-bold tracking-wide mb-4 flex items-center gap-2">
              <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'rgba(139,92,246,0.2)' }}>
                <FaFileAlt size={10} style={{ color: '#a78bfa' }} />
              </div>
              Documents & Links
            </h3>

            {/* Resume upload */}
            <div className="mb-4">
              <FieldLabel icon={<FaCloudUploadAlt size={10} style={{ color: '#a78bfa' }} />} text="Resume (PDF or Word)" />
              <label
                htmlFor="resume"
                className="flex items-center justify-center gap-3 w-full px-4 py-4 bg-WHITE/[0.04] border-2 border-dashed border-WHITE/15 rounded-xl text-WHITE/50 text-sm cursor-pointer hover:bg-WHITE/[0.06] hover:border-WHITE/25 transition-all duration-200"
              >
                <FaCloudUploadAlt size={18} style={{ color: '#a78bfa' }} />
                <span>{resumeFile ? resumeFile.name : 'Click to upload or drag & drop'}</span>
              </label>
              <input type="file" id="resume" accept=".pdf,.doc,.docx" onChange={handleFileChange} className="hidden" />
              {resumeFile && <p className="text-WHITE/40 text-xs mt-1.5">{(resumeFile.size / 1024).toFixed(0)} KB</p>}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <FieldLabel icon={<FaLink size={10} style={{ color: '#a78bfa' }} />} text="Portfolio URL" />
                <input type="text" id="portfolioUrl" name="portfolioUrl" value={formData.portfolioUrl} onChange={handleChange} className={inputBase} placeholder="https://yourportfolio.com" />
              </div>
              <div>
                <FieldLabel icon={<FaLinkedin size={10} style={{ color: '#a78bfa' }} />} text="LinkedIn URL" />
                <input type="text" id="linkedInUrl" name="linkedInUrl" value={formData.linkedInUrl} onChange={handleChange} className={inputBase} placeholder="linkedin.com/in/yourname" />
              </div>
            </div>
          </div>
        )}

        {/* Additional Info — careers only */}
        {!isCollegeAmbassador && (
          <div className="bg-WHITE/[0.04] rounded-2xl p-5 border border-WHITE/10">
            <h3 className="text-WHITE text-sm font-bold tracking-wide mb-4 flex items-center gap-2">
              <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.2)' }}>
                <FaCommentDots size={10} style={{ color: '#fbbf24' }} />
              </div>
              Additional Information
            </h3>

            <div className="mb-4">
              <FieldLabel icon={<FaBullhorn size={10} style={{ color: '#fbbf24' }} />} text="How did you hear about us?" />
              <select id="howDidYouHear" name="howDidYouHear" value={formData.howDidYouHear} onChange={handleChange} className={selectBase}>
                <option value="" className="bg-BLACK text-WHITE/60">Select</option>
                {HOW_DID_YOU_HEAR_OPTIONS.map((opt) => (
                  <option key={opt} value={opt} className="bg-BLACK">{opt}</option>
                ))}
              </select>
            </div>

            <div>
              <FieldLabel icon={<FaCommentDots size={10} style={{ color: '#fbbf24' }} />} text="Cover Letter" />
              <textarea
                id="coverLetter"
                name="coverLetter"
                value={formData.coverLetter}
                onChange={handleChange}
                maxLength={10000}
                rows={4}
                className={`${inputBase} resize-none`}
                placeholder="Tell us about yourself and why you'd like to join..."
              />
              <p className="text-WHITE/30 text-xs mt-1">{formData.coverLetter.length} / 10,000</p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="group relative w-full rounded-2xl overflow-hidden transition-all duration-500 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="absolute inset-0 rounded-2xl" style={{ background: 'conic-gradient(from 90deg, #3b82f6, #6366f1, #0ea5e9, #ffffff, #3b82f6)', animation: 'ring-spin 3s linear 1 forwards', padding: '1.5px' }}>
            <div className="w-full h-full rounded-2xl" style={{ background: 'rgba(59,130,246,0.08)' }} />
          </div>
          <div className="absolute -inset-1 rounded-2xl opacity-0 group-hover:opacity-30 transition-opacity duration-500 pointer-events-none" style={{ background: 'conic-gradient(from 90deg, #3b82f6, #6366f1, #3b82f6)', filter: 'blur(14px)' }} />
          <div className="relative flex items-center justify-center gap-3 px-8 py-4 rounded-2xl backdrop-blur-2xl" style={{ background: 'rgba(0,0,0,0.55)' }}>
            {loading ? (
              <>
                <FaSpinner className="animate-spin text-WHITE" size={18} />
                <span className="text-WHITE font-bold text-base tracking-wide">Submitting...</span>
              </>
            ) : (
              <>
                <div className="w-8 h-8 rounded-full flex items-center justify-center border" style={{ background: 'rgba(59,130,246,0.2)', borderColor: 'rgba(59,130,246,0.3)' }}>
                  <FaPaperPlane size={13} style={{ color: '#60a5fa' }} />
                </div>
                <span className="font-bold text-base tracking-wide" style={{ color: '#60a5fa' }}>Submit Application</span>
              </>
            )}
          </div>
        </button>
      </form>

      {/* Thank You Modal */}
      {showThankYou && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(20px)' }}>
          <div className="relative rounded-2xl overflow-hidden max-w-md w-full animate-fade-in" style={{ padding: '1.5px' }}>
            <div className="absolute inset-0 rounded-2xl" style={{ background: 'conic-gradient(from 90deg, #22c55e, #4ade80, #86efac, #ffffff, #22c55e)', animation: 'ring-spin 3s linear 1 forwards' }} />
            <div className="relative bg-BLACK/95 backdrop-blur-2xl rounded-2xl p-8 lg:p-10 text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.15)' }}>
                <FaCheckCircle size={36} style={{ color: '#4ade80' }} />
              </div>
              <h3 className="text-2xl font-bold text-WHITE mb-3">Application Received</h3>
              <p className="text-WHITE/60 text-sm mb-6 leading-relaxed">
                Thank you for your interest in CollectedAF. Our team will review your application and get back to you soon.
              </p>
              <button
                onClick={() => setShowThankYou(false)}
                className="group relative rounded-xl overflow-hidden transition-all duration-300 active:scale-[0.97]"
              >
                <div className="absolute inset-0 rounded-xl" style={{ background: 'conic-gradient(from 0deg, #22c55e, #4ade80, #22c55e)', padding: '1.5px' }}>
                  <div className="w-full h-full rounded-xl" style={{ background: 'rgba(0,0,0,0.7)' }} />
                </div>
                <div className="relative px-8 py-3 rounded-xl backdrop-blur-xl" style={{ background: 'rgba(0,0,0,0.5)' }}>
                  <span className="font-bold text-sm" style={{ color: '#4ade80' }}>Close</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MarketingJobForm;
